import { AxiosResponse, AxiosError } from "axios";
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import { ResponseGetEvacuationDrillMode } from "@/@types/evacuation-drill";
import { isResponseGetEvacuationDrillMode } from "@/usecase/evacuation-drill";
import $http from "../api-datastores/http";
import store from "@/store";
import i18n from "@/i18n";
const ErrorApi = { message: "API error", name: "" };
const ErrorParameter = { message: "Parameter error", name: "" };

import { Auth } from "@/models/auth";
import { handleError } from "./shared";
import { cleanParams } from "@/util/url-query.helper";
import { MapsMapType } from "@/util/filter-genre.helper";
const auth = new Auth();

/** @remarks place category (genre) */
export async function getGenreList(countryName: string): Promise<PortalAdapterApi.ResponseGetPlaceCategoryList> {
  const language = i18n.locale;

  return await $http
    .get(`/v1/place-categories`, { params: { language, countryName } })
    .then((response: AxiosResponse) =>
      response.status === 200
        ? (response.data as PortalAdapterApi.ResponseGetPlaceCategoryList)
        : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks access areaName directly */
export async function getAreaInfoByName({
  areaName,
  mapsMapType,
  isOpendata,
}: {
  areaName: string;
  mapsMapType?: string;
  isOpendata?: boolean;
}): Promise<PortalAdapterApi.TopArea> {
  const language = i18n.locale;
  return await $http
    .get(`/v1/places/area`, {
      params: {
        language,
        unixtime: Date.now() / 1000,
        mapsMapType,
        isOpendata,
        name: encodeURIComponent(areaName),
      },
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.TopArea) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks Same building list */
export async function fetchSameBuildingList(
  buildingIdHash: string,
): Promise<PortalAdapterApi.ResponseGetPublicPlace[]> {
  const language = i18n.locale;
  return await $http
    .get(`/v1/buildings/${buildingIdHash}/places`, {
      params: { language, unixtime: Date.now() / 1000 },
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.ResponseGetPublicPlace[]) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks Top Area Places */
export async function fetchTopAreaCountryList(): Promise<PortalAdapterApi.ResponseGetTopAreaCountryList> {
  const language = i18n.locale;
  return await $http
    .get("/v1/places/top-areas/countries", { params: { language } })
    .then((response: AxiosResponse) =>
      response.status === 200
        ? (response.data as PortalAdapterApi.ResponseGetTopAreaCountryList)
        : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/**
 * @remarks 投票所のエリアあるかどうか
 * */
export async function fetchHasPollPlaces(mapsMapType: string = "polling-place"): Promise<boolean> {
  return await $http
    .get("/v1/places/top-areas/existence", {
      params: {
        mapsMapType,
      },
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? Promise.resolve(Boolean(response.data)) : Promise.resolve(false),
    )
    .catch(() => Promise.resolve(false));
}
/**
 * @remarks mapsMapTypeに依頼するエリアfetch
 * */
export async function fetchTopAreaPlaceList(
  countryName: string = "jp",
  mapsMapType: string = "normal",
): Promise<PortalAdapterApi.ResponseGetTopAreaList> {
  const language = i18n.locale;
  return await $http
    .get("/v1/places/top-areas", {
      params: {
        language,
        countryName,
        mapsMapType,
      },
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.ResponseGetTopAreaList) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

export type FilterParams = {
  // filters
  vacant?: string;
  favorite?: string;
  genre: string[]; // name of query parameter
  genreCategoryList?: string[];
  isOpendata?: boolean;
  source?: string;
  mapsMapType?: string; // carried in the URL so the map mode survives reload
};

export async function getPlaceListByLocation({
  lat,
  lng,
  radius,
  countryName,
  params,
  areaName,
  mapType,
  isOpendata,
  placeName,
}: {
  lat: number;
  lng: number;
  radius: number; // meter
  countryName: string;
  params: FilterParams;
  areaName?: string;
  mapType?: string;
  isOpendata?: boolean;
  placeName?: string;
}): Promise<PortalAdapterApi.ResponseGetPublicPlace[]> {
  const vacant: string | undefined = params?.vacant,
    favorite: string | undefined = params?.favorite,
    genre: string | undefined =
      params.genre.length === 0 || params.genre.includes("all") ? undefined : params.genre.join(","),
    genreCategoryList: string | undefined = params.genreCategoryList?.join(",") || undefined,
    language: string = i18n.locale;

  if (isNaN(lat) || isNaN(lng) || isNaN(radius)) {
    return Promise.reject(ErrorParameter);
  }

  radius = radius / 1000; // change to send km

  return await $http
    .get("/v1/places", {
      params: cleanParams({
        language,
        baseLocation: `${lat},${lng}`,
        radius,
        unixtime: Date.now() / 1000,
        countryName,
        vacant,
        favorite,
        genre,
        genreCategoryList,
        areaName,
        mapsMapType: mapType,
        isOpendata,
        placeName,
      }),
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.ResponseGetPublicPlace[]) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => {
      return error.response?.status === 404 ? Promise.resolve([]) : handleError(error);
    });
}

export async function getGenreCategoryList(genreName: string): Promise<PortalAdapterApi.GenreCategoryType> {
  const language = i18n.locale;
  return await $http
    .get(`/v1/genre/${genreName}/categoryList`, { params: { language } })
    .then((response: AxiosResponse) => (response.status === 200 ? response.data : Promise.reject(ErrorApi)))
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks 空き状態 */
export async function getFacilityVacancyListByPlaceIdHashList(
  placeIdHashList: string[],
): Promise<PortalAdapterApi.ResponseGetMergedDisplayVacancy[]> {
  if (placeIdHashList.length <= 0) {
    return Promise.reject(ErrorParameter);
  }
  const language: string = store.state.data.language;
  const placeIdHashs: string = placeIdHashList.join(",");
  return await $http
    .get(`/v1/places/${placeIdHashs}/vacancy`, {
      params: { language, target: "portal", unixtime: Date.now() / 1000 },
    })
    .then((response: AxiosResponse) =>
      response.status === 200
        ? (response.data as PortalAdapterApi.ResponseGetMergedDisplayVacancy[])
        : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks 詳細ページ */
export async function fetchPlaceDetailByPlaceIdHash(
  placeIdHash: string,
  isOpendata?: boolean,
): Promise<PortalAdapterApi.ResponseGetPlaceDetail> {
  if (placeIdHash.length === 0) {
    return Promise.reject(ErrorParameter);
  }
  const language = i18n.locale;
  return await $http
    .get(`/v1/place/${placeIdHash}/detail`, {
      params: { language, unixtime: Date.now() / 1000, isOpendata },
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.ResponseGetPlaceDetail) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks ショップの画像 */
export async function fetchPlaceImagesByPlaceIdHash({
  placeIdHash,
  page = 1,
  isOpendata,
}: {
  placeIdHash: string;
  page: number;
  isOpendata: boolean;
}): Promise<PortalAdapterApi.ResponseGetShopImage[]> {
  if (placeIdHash.length === 0) {
    return Promise.reject(ErrorParameter);
  }
  return await $http
    .get(`/v1/place/${placeIdHash}/images/collections`, {
      params: { page, length: store.state.ui.IMGPAGENUMBER, isOpendata },
    })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.ResponseGetShopImage[]) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/** @remarks Couponページ */
export async function fetchCouponListByPlaceIdHash(
  placeIdHash: string,
): Promise<PortalAdapterApi.ResponseGetPublicCouponForPortal[]> {
  if (placeIdHash.length === 0) {
    return Promise.reject(ErrorParameter);
  }
  const language = i18n.locale;
  return await $http
    .get(`/v1/place/${placeIdHash}/coupons`, {
      params: { language, unixtime: Date.now() / 1000 },
    })
    .then((response: AxiosResponse) =>
      response.status === 200
        ? (response.data as PortalAdapterApi.ResponseGetPublicCouponForPortal[])
        : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

export async function postCampaignCouponCheckIn(placeId: string, couponId: string): Promise<boolean> {
  return await $http
    .post(`/v1/place/${placeId}/campaign-coupon`, {
      couponIdHash: couponId,
      requestUnixtime: Date.now() / 1000,
    } as PortalAdapterApi.RequestPostCouponHistory)
    .then((response: AxiosResponse) => {
      return response.status === 200 || response.status === 204;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}

/** @remarks お知らせ */
export async function fetchAnnouncementNumber(): Promise<number> {
  return await $http
    .get("/v1/notices/total-number")
    .then((response: AxiosResponse) => (response.status === 200 ? (response.data as number) : Promise.reject(ErrorApi)))
    .catch((error: AxiosError) => handleError(error));
}

export async function fetchAnnouncementList(): Promise<PortalAdapterApi.ResponseGetNotice[]> {
  const language = i18n.locale;
  return await $http
    .get(`/v1/notices`, { params: { language } })
    .then((response: AxiosResponse) =>
      response.status === 200 ? (response.data as PortalAdapterApi.ResponseGetNotice[]) : Promise.reject(ErrorApi),
    )
    .catch((error: AxiosError) => handleError(error));
}

/**
 * @remarks Login functions
 * */

export async function login({
  email,
  password,
}: PortalAdapterApi.RequestPostLogin): Promise<PortalAdapterApi.ResponsePostLogin> {
  return await $http
    .post(`/v1/user/signin`, { email, password })
    .then((response: AxiosResponse) => {
      if (!auth.isResponsePostLogin(response.data)) {
        throw new Error();
      }
      return response.data as PortalAdapterApi.ResponsePostLogin;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
// accessToken is attached in http.ts
export async function logout(): Promise<boolean> {
  return await $http
    .post(`/v1/user/signout`)
    .then((response: AxiosResponse) => {
      return response.status === 200 || response.status === 401;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}

export async function postRenewSession(): Promise<PortalAdapterApi.ResponsePostLogin> {
  return await $http
    .post(`/v1/user/session`, {
      refreshToken: localStorage.getItem("session/tokens/refreshToken"),
    })
    .then((response: AxiosResponse) => {
      if (!auth.isResponsePostLogin(response.data)) {
        throw new Error();
      }
      return response.data as PortalAdapterApi.ResponsePostLogin;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}

export async function signup({ email }: { email: string }): Promise<boolean> {
  const language: string = store.state.data.language;
  return await $http
    .post(`/v1/user/signup`, { email, language })
    .then((response: AxiosResponse) => {
      if (response.status >= 400) throw new Error();
      return response.status === 200 || response.status === 204;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
export async function activate({ email, confirmationCode }: PortalAdapterApi.RequestPostActivate): Promise<number> {
  return await $http
    .post(`/v1/user/activate`, { email, confirmationCode })
    .then((response: AxiosResponse) => {
      return response.status as number;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
export async function forgotPassword({ email }: PortalAdapterApi.RequestPostForgotPassword): Promise<boolean> {
  return await $http
    .post(`/v1/user/forgot-password`, { email })
    .then((response: AxiosResponse) => {
      if (response.status >= 400) throw new Error();
      return response.status === 200 || response.status === 204;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
export async function changePassword({
  oldPassword,
  newPassword,
}: PortalAdapterApi.RequestPostChangePassword): Promise<number> {
  return await $http
    .post(`/v1/user/change-password`, { oldPassword, newPassword })
    .then((response: AxiosResponse) => {
      return response.status as number;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
export async function resetPassword({
  email,
  newPassword,
  confirmationCode,
}: PortalAdapterApi.RequestPostResetPassword): Promise<boolean> {
  return await $http
    .post(`/v1/user/reset-password`, { email, newPassword, confirmationCode })
    .then((response: AxiosResponse) => {
      if (response.status >= 400) throw new Error();
      return response.status === 200 || response.status === 204;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
// accessToken is attached in http.ts
export async function getUserId(): Promise<{ userId: number }> {
  const response: AxiosResponse<{ userId: number }> = await $http.get(`/v1/user/id`);

  if (response.status >= 400) throw new Error();
  return response.data;
}
// accessToken is attached in http.ts
export async function deleteUser() {
  return await $http
    .delete(`/v1/user`)
    .then((response: AxiosResponse) => {
      if (response.status >= 400) throw new Error();
      return response.status === 200 || response.status === 204;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}

/** @remarks Favorite */
export async function getFavoriteList(): Promise<PortalAdapterApi.ResponseGetPublicPlace[]> {
  const language = i18n.locale;
  return await $http
    .get(`/v1/favorites`, { params: { language, unixtime: Date.now() / 1000 } })
    .then((response: AxiosResponse) => {
      return response.data as PortalAdapterApi.ResponseGetPublicPlace[];
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
export async function createFavoritePlace(placeIdHash: string, isOpendata?: boolean): Promise<boolean> {
  return await $http
    .post(`/v1/favorite`, { placeIdHash, isOpendata })
    .then((response: AxiosResponse) => {
      return response.status === 201;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}
export async function deleteFavoritePlace(placeIdHash: string, isOpendata?: boolean): Promise<boolean> {
  return await $http
    .delete(`/v1/favorite/${placeIdHash}?isOpendata=${isOpendata}`)
    .then((response: AxiosResponse) => {
      return response.status === 204;
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}

/** @remarks realtime update evacuation drills */
export async function getEvacuationDrill(
  placeIdHashList: string[],
): Promise<ResponseGetEvacuationDrillMode[] | undefined> {
  return await $http
    .get(`/v1/places/${placeIdHashList}/evacuation-drill`, {
      params: { unixtime: Date.now() / 1000 },
    })
    .then((response: AxiosResponse) => {
      try {
        if (typeof response.data === "object" && response.data.length > 0) {
          const validate = response.data.every((d: ResponseGetEvacuationDrillMode) =>
            isResponseGetEvacuationDrillMode(d),
          );
          return validate ? response.data : undefined;
        } else {
          return undefined;
        }
      } catch {
        return Promise.reject(undefined);
      }
    })
    .catch((error: AxiosError) => {
      return handleError(error);
    });
}

/** @remarks
 * Search cities from open data evacuation-centers
 * Opendata避難所の市区町村を検索 */
export async function getEvacuationCitiesByName({
  cityName,
  mapsMapType,
}: {
  cityName: string;
  mapsMapType: MapsMapType;
}): Promise<(PortalAdapterApi.ResponseGetPublicPlace & { enName: string })[]> {
  try {
    const response: AxiosResponse = await $http.get(`/v1/places/evacuation-centers/cities`, {
      params: { cityName, mapsMapType },
    });
    return response.data ?? [];
  } catch {
    return [];
  }
}

export default {
  // filter
  getGenreCategoryList,
  // favorite
  getFavoriteList,
  createFavoritePlace,
  deleteFavoritePlace,
  // places
  getGenreList,
  getAreaInfoByName,
  fetchSameBuildingList,
  fetchTopAreaCountryList,
  fetchTopAreaPlaceList,
  fetchCouponListByPlaceIdHash,
  fetchAnnouncementNumber,
  fetchAnnouncementList,
  getPlaceListByLocation,
  getFacilityVacancyListByPlaceIdHashList,
  fetchPlaceDetailByPlaceIdHash,
  fetchPlaceImagesByPlaceIdHash,
  //login
  login,
  logout,
  postRenewSession,
  signup,
  changePassword,
  forgotPassword,
  resetPassword,
  activate,
};
